import { ExternalLink, MapPin, Briefcase, Rss, Twitter, Globe, Bookmark, Eye } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RatingStars } from '@/components/community/RatingStars';
import { useJobBoardFavorites } from '@/hooks/useFavorites';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { JobBoard } from '@/hooks/useJobBoards';

interface JobBoardCardProps {
  jobBoard: JobBoard;
}

export function JobBoardCard({ jobBoard }: JobBoardCardProps) {
  const { jobBoardFavorites, toggleJobBoardFavorite } = useJobBoardFavorites();
  const { toast } = useToast();
  const categoryLabel = jobBoard.category === 'design_niche' ? 'Design Niche' : 'General';
  const isFavorite = jobBoardFavorites.includes(jobBoard.id);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleJobBoardFavorite(jobBoard.id);
    toast({
      title: isFavorite ? "Removed from favorites" : "Added to favorites",
      description: isFavorite 
        ? `${jobBoard.name} removed from your favorites`
        : `${jobBoard.name} added to your favorites`,
    });
  };
  
  return (
    <Card className="group hover:shadow-md transition-all duration-200 border-border bg-card">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3 flex-1">
            {jobBoard.logo_url ? (
              <img
                src={jobBoard.logo_url}
                alt={`${jobBoard.name} logo`}
                className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 text-primary" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors line-clamp-2 break-words">
                {jobBoard.name}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary" className="text-xs">
                  {categoryLabel}
                </Badge>
                {jobBoard.remote_focus && (
                  <Badge variant="secondary" className="text-xs">
                    Remote
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(jobBoard.url, '_blank')}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Description */}
        <div className="h-[4.5rem] mb-4">
          <p className="text-sm text-muted-foreground line-clamp-3 break-words">
            {jobBoard.description || ''}
          </p>
        </div>

        {/* New Tags from categorization */}
        <div className="h-[3rem] mb-4">
          <div className="flex flex-wrap gap-2">
            {/* Job Types */}
            {jobBoard.job_type && jobBoard.job_type.length > 0 && (
              jobBoard.job_type.slice(0, 2).map((type) => (
                <Badge key={`jobtype-${type}`} variant="secondary" className="text-xs capitalize">
                  {type.replace('_', ' ')}
                </Badge>
              ))
            )}
            
            {/* Niche */}
            {jobBoard.niche && jobBoard.niche.length > 0 && (
              jobBoard.niche.slice(0, 1).map((n) => (
                <Badge key={`niche-${n}`} variant="secondary" className="text-xs capitalize">
                  {n}
                </Badge>
              ))
            )}
            
            {/* Work Mode */}
            {jobBoard.work_mode && jobBoard.work_mode.length > 0 && (
              jobBoard.work_mode.slice(0, 1).map((mode) => (
                <Badge key={`workmode-${mode}`} variant="secondary" className="text-xs capitalize">
                  {mode}
                </Badge>
              ))
            )}
            
            {/* Geography */}
            {jobBoard.geography && jobBoard.geography.length > 0 && (
              jobBoard.geography.slice(0, 1).map((geo) => (
                <Badge key={`geo-${geo}`} variant="secondary" className="text-xs capitalize">
                  {geo}
                </Badge>
              ))
            )}

            {/* Legacy tags */}
            {jobBoard.tags && jobBoard.tags.length > 0 && (
              jobBoard.tags.slice(0, 2).map((tag) => (
                <Badge key={`tag-${tag}`} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))
            )}
          </div>
        </div>

        {/* Community Features */}
        <div className="mb-4 flex items-center justify-between">
          <RatingStars entityType="job_board" entityId={jobBoard.id} compact={true} />
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleFavoriteClick}
              className={cn(
                "h-6 w-6 p-0 opacity-60 hover:opacity-100 transition-all duration-base",
                isFavorite && "opacity-100 text-primary"
              )}
            >
              <Bookmark className={cn("h-3 w-3", isFavorite && "fill-current")} />
            </Button>
          </div>
        </div>

        {/* Footer Info */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            {jobBoard.country_scope && (
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                <span>{jobBoard.country_scope}</span>
              </div>
            )}
            
            {jobBoard.price_posting && (
              <div className="flex items-center gap-1">
                <span>💰</span>
                <span>{jobBoard.price_posting}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {jobBoard.has_rss && jobBoard.rss_url && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(jobBoard.rss_url, '_blank')}
                className="h-6 w-6 p-0"
              >
                <Rss className="w-3 h-3" />
              </Button>
            )}
            
            {jobBoard.twitter_handle && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(`https://twitter.com/${jobBoard.twitter_handle}`, '_blank')}
                className="h-6 w-6 p-0"
              >
                <Twitter className="w-3 h-3" />
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(jobBoard.url, '_blank')}
              className="h-6 w-6 p-0"
            >
              <Globe className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Open Button */}
        <div className="mt-4">
          <Button 
            onClick={() => window.open(jobBoard.url, '_blank')}
            variant="outline"
            className="w-full"
            size="sm"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Open
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}